<template>
  <v-container>
    <v-row>
      <v-col cols="12" sm="6">
        <h1>Options</h1>
        <v-form v-model="appIdValid" ref="appIDForm">
          <v-container>
            <v-row>
              <v-col cols="12">
                <v-text-field
                  v-model="appId"
                  :rules="appIdRules"
                  :counter="APP_ID_LENGTH"
                  label="App ID"
                  required
                ></v-text-field>
              </v-col>
            </v-row>
          </v-container>
        </v-form>

        <div>
          <v-row class="mt-4">
            <v-col cols="8" class="my-auto">
              <h3>
                Query Parameters
                <span class="subheading font-weight-regular">(Optional)</span>
              </h3>
            </v-col>
            <v-col cols="4" class="text-right">
              <v-btn fab dark small color="#E60000" @click="addQueryParam">
                <v-icon dark> mdi-plus </v-icon>
              </v-btn>
            </v-col>
          </v-row>

          <v-form
            ref="queryParamsForm"
            v-model="queryParamsValid"
            lazy-validation
            id="query-params-form"
          >
            <v-row v-for="(queryParam, i) in queryParams" :key="i">
              <v-col cols="5">
                <v-text-field
                  v-model="queryParam.key"
                  :rules="keyRules"
                  label="Key"
                  required
                ></v-text-field>
              </v-col>

              <v-col cols="5">
                <v-text-field
                  v-model="queryParam.value"
                  :rules="valueRules"
                  label="Value"
                  required
                ></v-text-field>
              </v-col>
              <v-col cols="1" class="text-left my-auto">
                <v-btn
                  fab
                  dark
                  x-small
                  color="#08081B"
                  @click="removeQueryParam(i)"
                >
                  <v-icon dark> mdi-minus </v-icon>
                </v-btn>
              </v-col>
            </v-row>
          </v-form>
        </div>
        <div class="text-center mt-1">
          <v-btn
            :loading="resultsLoading"
            x-large
            color="#E60000"
            dark
            rounded
            @click="generateResults"
          >
            Generate
          </v-btn>
        </div>
      </v-col>
      <v-col cols="12" sm="6">
        <h1>Results</h1>

        <v-row dense>
          <v-col cols="12" class="mt-2">
            <v-card
              color="#222739"
              dark
              class="mb-3"
              v-for="(textLink, i) in textLinks"
              :key="i"
            >
              <v-card-title class="text-h5">
                {{ textLink.type }}
              </v-card-title>

              <v-card-subtitle> {{ textLink.value }}</v-card-subtitle>

              <v-card-actions v-if="textLink.value">
                <v-btn text @click="copyToClipboard(textLink.value)">
                  Copy to Clipboard</v-btn
                >
              </v-card-actions>
            </v-card>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" sm="6" v-for="(qrLink, i) in qrLinks" :key="i">
            <v-card class="mb-3">
              <v-card-title class="text-h5">
                {{ qrLink.type }}
              </v-card-title>
              <div v-if="qrLink.value" class="text-center pb-5 mb-5">
                <qrcode-vue
                  :value="qrLink.value"
                  size="200"
                  level="H"
                  margin="5"
                  background="#ffffff"
                  foreground="#08081B"
                />
              </div>
            </v-card>
          </v-col>
        </v-row>
      </v-col>

      <v-snackbar
        dark
        color="#08081B"
        v-model="snackbar"
        :timeout="snackbarTimeout"
      >
        {{ snackbarText }}
      </v-snackbar>
    </v-row>
  </v-container>
</template>

<script>
import {
  APP_ID_LENGTH,
  PROD_URL_BASE,
  SANDBOX_URL_BASE,
  INTERNAL_QR_CODE_URL_BASE,
  VODAPAY_URL_SCHEME,
} from "../constants/constants";
import QrcodeVue from "qrcode.vue";

export default {
  name: "LinkGeneration",
  components: {
    QrcodeVue,
  },
  data: () => ({
    appIdValid: false,
    queryParamsValid: false,
    appId: "",
    appIdRules: [
      (v) => !!v || "App ID is required",
      (v) =>
        v.length === APP_ID_LENGTH ||
        "App ID must be " + APP_ID_LENGTH + " characters",
      (v) =>
        !!v.match(/^[0-9]+$/) ||
        "App ID must consist of numeric characters only",
    ],
    keyRules: [(v) => !!v || "Key is required"],
    valueRules: [(v) => !!v || "Value is required"],
    queryParams: [],
    APP_ID_LENGTH,
    textLinks: [
      {
        type: "Shortened Link",
        value: "",
      },
      {
        type: "Full Link",
        value: "",
      },
      {
        type: "Daily Deals Link",
        value: "",
      },
      {
        type: "Sandbox Link (For Developers)",
        value: "",
      },
    ],
    qrLinks: [
      {
        type: "External QR",
        value: "",
      },
      {
        type: "Internal QR",
        value: "",
      },
    ],
    snackbar: false,
    snackbarTimeout: 2000,
    snackbarText: "",
    resultsLoading: false,
  }),
  methods: {
    addQueryParam() {
      this.queryParams.push({
        key: "",
        value: "",
      });
    },
    removeQueryParam(arrPos) {
      this.queryParams.splice(arrPos, 1);
    },
    generateResults() {
      if (
        this.$refs.appIDForm.validate() &&
        this.$refs.queryParamsForm.validate()
      ) {
        this.resultsLoading = true;
        let payload = "action=miniapp&miniappId=" + this.appId;
        let htmlLinkPayload = payload;
        let internalQRURL = INTERNAL_QR_CODE_URL_BASE + this.appId;
        if (this.queryParams.length > 0) {
          let queryString = "";
          this.queryParams.forEach((paramObj, i) => {
            queryString += paramObj.key + "=" + paramObj.value;
            if (i != this.queryParams.length - 1) {
              queryString += "&";
            }
          });
          const encodedQueryParam = "&query=" + encodeURIComponent(queryString);
          payload += encodedQueryParam;
          internalQRURL += encodedQueryParam;
          htmlLinkPayload += encodedQueryParam;
        }
        const externalLink = PROD_URL_BASE + encodeURIComponent(payload);
        const sandboxExternalLink =
          SANDBOX_URL_BASE + encodeURIComponent(payload);
        this.textLinks[1].value = externalLink;
        this.textLinks[2].value = VODAPAY_URL_SCHEME + htmlLinkPayload;
        this.textLinks[3].value = sandboxExternalLink;
        this.qrLinks[0].value = externalLink;
        this.qrLinks[1].value = internalQRURL;
        this.createShortenedLink(externalLink);
      }
    },
    copyToClipboard(text) {
      navigator.clipboard.writeText(text);
      this.snackbarText = "Link copied to clipboard";
      this.snackbar = true;
    },
    createShortenedLink(link) {
      // POST request using fetch with set headers
      const requestOptions = {
        method: "POST",
        headers: {
          Authorization: "Bearer a6c5fbff817bf0168f250b23f2a621fc75fa6a41",
          "Content-Type": "application/json",
        },
        body:
          '{ "long_url": "' +
          link +
          '", "domain": "bit.ly", "group_guid": "Bla3e2XTspO" }',
      };
      fetch("https://api-ssl.bitly.com/v4/shorten", requestOptions)
        .then((response) => response.json())
        .then((data) => {
          this.textLinks[0].value = data.link;
          this.resultsLoading = false;
          this.snackbarText = "Results generated succesfully";
          this.snackbar = true;
        });
    },
  },
};
</script>

<style scoped>
#query-params-form {
  min-height: 5vh;
  max-height: 40vh;
  overflow-y: auto;
  overflow-x: hidden;
}
@media only screen and (min-width: 768px) {
  #query-params-form {
    height: 60vh;
  }
}
</style>
